import { skipToken, useQuery } from "@apollo/client/react";
import { useParams } from "react-router-dom";
import { useAnalyticsRoot } from "@evg-ui/lib/analytics/hooks";
import { useQueryParam } from "@evg-ui/lib/hooks";
import { AnalyticsIdentifier } from "analytics/types";
import { TaskTimingMetric } from "constants/externalResources/honeycomb";
import { slugs } from "constants/routes";
import {
  TaskQuery,
  TaskQueryVariables,
  TaskSortCategory,
  TaskTestCountQuery,
  TaskTestCountQueryVariables,
  TestSortCategory,
} from "gql/generated/types";
import { TASK, TASK_TEST_COUNT } from "gql/queries";
import { CommitType } from "pages/task/ActionButtons/StepbackMenu/types";
import {
  CreatedNotificationAction,
  ViewedNotificationModalAction,
} from "types/subscription";
import { LogTypes, RequiredQueryParams } from "types/task";

type LogViewer = "raw" | "html" | "parsley" | "download";
type Action =
  | { name: "Filtered tests table"; "filter.by": string | string[] }
  | {
      name: "Sorted tests table";
      "sort.by": TestSortCategory | TestSortCategory[];
    }
  | {
      name: "Sorted execution tasks table";
      "sort.by": TaskSortCategory | TaskSortCategory[];
    }
  | {
      name: "Clicked restart task button";
      "task.is_display_task": false;
    }
  | {
      name: "Clicked restart task button";
      allTasks: boolean;
      "task.is_display_task": true;
    }
  | {
      name: "Clicked execution tasks table link";
    }
  | { name: "Clicked schedule task button" }
  | { name: "Clicked abort task button" }
  | { name: "Clicked disable task button" }
  | { name: "Clicked enable task button" }
  | { name: "Clicked override dependencies button" }
  | { name: "Changed task priority"; "task.priority": number }
  | { name: "Clicked unschedule task button" }
  | { name: "Changed page size" }
  | { name: "Changed tab"; tab: string }
  | { name: "Changed execution" }
  | { name: "Clicked log link"; "log.type": LogTypes; "log.viewer": LogViewer }
  | {
      name: "Clicked test log link";
      "log.viewer": LogViewer;
      "test.status": string;
    }
  | {
      name: "Clicked quarantine test button";
      "test.name": string;
      "test.task_id": string;
    }
  | {
      name: "Clicked unquarantine test button";
      "test.name": string;
      "test.task_id": string;
    }
  | {
      name: "Viewed skipped tests modal";
      "tests.skipped_count": number;
    }
  | {
      name: "Clicked download skipped tests JSON button";
      "tests.skipped_count": number;
    }
  | { name: "Clicked skipped tests details button" }
  | { name: "Clicked annotation link"; "link.text": string }
  | { name: "Changed log preview type"; "log.type": LogTypes }
  | ViewedNotificationModalAction
  | CreatedNotificationAction
  | { name: "Clicked see history link" }
  | { name: "Clicked metadata link"; "link.type": string }
  | {
      name: "Clicked task file link";
      "parsley.is_available": boolean;
      "file.name": string;
    }
  | {
      name: "Clicked task file Parsley link";
      "file.name": string;
    }
  | {
      name: "Clicked task file associated link";
      "file.name": string;
      "link.name": string;
    }
  | { name: "Clicked relevant commit"; type: CommitType }
  | { name: "Redirected to default tab"; tab: string }
  | {
      name: "Clicked task timing link";
      metric: TaskTimingMetric;
      only_commits: boolean;
      only_successful: boolean;
    }
  | {
      name: "Clicked review task";
      reviewed: boolean;
    }
  | { name: "Clicked cost details button" }
  | { name: "Clicked to dismiss debug spawn host guide cue" };

export const useTaskAnalytics = () => {
  const { [slugs.taskId]: taskId } = useParams();

  const [execution] = useQueryParam(RequiredQueryParams.Execution, 0);
  const { data: eventData } = useQuery<TaskQuery, TaskQueryVariables>(
    TASK,
    taskId
      ? {
          variables: { taskId, execution },
          errorPolicy: "all",
          fetchPolicy: "cache-first",
        }
      : skipToken,
  );
  const { data: taskTestCountData } = useQuery<
    TaskTestCountQuery,
    TaskTestCountQueryVariables
  >(
    TASK_TEST_COUNT,
    taskId && execution !== null
      ? {
          variables: {
            taskId,
            execution,
          },
          fetchPolicy: "cache-first",
        }
      : skipToken,
  );
  const { failedTestCount } = taskTestCountData?.task || {};

  const {
    displayName,
    displayStatus,
    latestExecution,
    project,
    requester = "",
    startTime,
    status: taskStatus,
    versionMetadata: { isPatch } = { isPatch: false },
  } = eventData?.task || {};
  const { identifier } = project || {};
  const isLatestExecution = latestExecution === execution;

  // Normalize possible Date|string|number start time to ISO string.
  const taskStartTime = startTime
    ? new Date(startTime as Date | string | number).toISOString()
    : "";

  return useAnalyticsRoot<Action, AnalyticsIdentifier>("Task", {
    "task.display_status": displayStatus || "",
    "task.execution": execution,
    "task.failed_test_count": failedTestCount || "",
    "task.id": taskId || "",
    "task.is_latest_execution": isLatestExecution,
    "task.name": displayName || "",
    "task.project.identifier": identifier || "",
    "task.start_time": taskStartTime,
    "task.status": taskStatus || "",
    "version.is_patch": isPatch,
    "version.requester": requester,
  });
};
